import { useEffect, useRef, useState } from 'react';

/**
 * Revela um bloco quando ele entra na viewport.
 *
 * Respeita `prefers-reduced-motion`: quem pediu menos movimento recebe o
 * conteúdo já visível, sem transição. Animação que ignora essa preferência
 * não é enfeite, é barreira de acessibilidade — e num caso extremo,
 * desencadeia enjoo em quem tem sensibilidade vestibular.
 */
export function useReveal<T extends HTMLElement = HTMLDivElement>() {
  const ref = useRef<T>(null);
  const [visivel, setVisivel] = useState(false);

  useEffect(() => {
    const semMovimento = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (semMovimento) {
      setVisivel(true);
      return;
    }

    const alvo = ref.current;
    if (!alvo) return;

    const observer = new IntersectionObserver(
      ([entrada]) => {
        if (entrada.isIntersecting) {
          setVisivel(true);
          observer.disconnect(); // uma vez só: não some ao rolar de volta
        }
      },
      // Dispara um pouco antes de entrar de fato, para a animação terminar
      // enquanto o bloco chega ao centro da tela.
      { rootMargin: '0px 0px -12% 0px', threshold: 0.05 },
    );

    observer.observe(alvo);
    return () => observer.disconnect();
  }, []);

  return { ref, visivel };
}

/** Classes da transição de entrada. */
export function revealClass(visivel: boolean, atraso = 0): string {
  return [
    'transition-all duration-700 ease-out motion-reduce:transition-none',
    visivel ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0',
    atraso ? `[transition-delay:${atraso}ms]` : '',
  ]
    .filter(Boolean)
    .join(' ');
}
